import React, { useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import { useForm } from "react-hook-form";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import CipHeader from "../Components/CipHeader";
import cogoToast from "cogo-toast";
import { kaReducer, Table } from "ka-table";
import { loadData, updateData, search } from "ka-table/actionCreators";
import { SortingMode, PagingPosition } from "ka-table/enums";
import Loader from "react-loader-spinner";
import "react-loader-spinner/dist/loader/css/react-spinner-loader.css";
import approvalQueService from "../services/approval-que-service";



const tablePropsInit = {
  columns: [
    { key: "checkbox", title: "", style: { width: 40 } },
    { key: "drg_name", title: "Drug", style: { width: 120 } },
    { key: "Description", title: "Description", style: { width: 160 } },
    { key: "Quantity", title: "Quantity" },
    { key: "category", title: "Category" },
    { key: "pre_Ord_Date", title: "Last Order Date" },
    { key: "LastOrderQty", title: "Last Order Quantity" },
    { key: "Actions", title: "Actions" },
  ],
  singleAction: loadData(),
  sortingMode: SortingMode.Single,
  rowKeyField: "id",
  paging: {
    enabled: true,
    pageIndex: 0,
    pageSize: 5,
    position: PagingPosition.Bottom,
  },
};

const ApprovalQue = () => {
  const [loading, setLoading] = useState(false);
  const [facList, setFacList] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [requests, setRequests] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [quantities, setQuantities] = useState({});
  const [qtyErrors, setQtyErrors] = useState({});
  const [tableProps, changeTableProps] = useState(tablePropsInit);

  const { register, setValue } = useForm({});

  const dispatch = async (action) => {
    changeTableProps((prevState) => kaReducer(prevState, action));
  };

  // ── Selection helpers ────────────────────────────────────────────────────

  const allIds = requests.map((r) => r.id);
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.has(id));

  const handleSelectAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(allIds));
  };

  const handleSelectRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // ── Custom cells ──────────────────────────────────────────────────────────

  const ActionsCell = ({ rowData }) => (
    <div className="inline-flex items-center gap-2">
      <button
        onClick={() => handleApprove([rowData.id])}
        className="bg-header_bg text-white text-xs font-semibold px-3 py-1 rounded focus:outline-none hover:opacity-80"
      >
        Approve
      </button>
      <button
        onClick={() => handleDeny([rowData.id])}
        className="text-white text-xs font-semibold px-3 py-1 rounded focus:outline-none hover:opacity-80"
        style={{ backgroundColor: "#e05a5a" }}
      >
        Deny
      </button>
    </div>
  );

  // ── Handlers ─────────────────────────────────────────────────────────────

  const loadFacilities = async () => {
    try {
      setLoading(true);
      const resp = await approvalQueService.getFacilities();
      setFacList(resp);
      if (resp.length === 1) {
        setValue("facility", resp[0].id);
        loadRequestsByFacility(resp[0].id);
      }
    } catch (e) {
      const { hide } = cogoToast.error("Error loading facilities.", {
        hideAfter: 15,
        onClick: () => { hide(); },
      });
    } finally {
      setLoading(false);
    }
  };

  const loadRequestsByFacility = async (facId) => {
    if (!facId || parseInt(facId, 10) === 0) {
      setShowTable(false);
      setRequests([]);
      setSelectedIds(new Set());
      setQuantities({});
      dispatch(updateData([]));
      return;
    }
    try {
      setLoading(true);
      const resp = await approvalQueService.getApprovalRequestsByFacility(facId);
      setRequests(resp);
      const initQtys = {};
      resp.forEach((r) => { initQtys[r.id] = r.qnty; });
      setQuantities(initQtys);
      setSelectedIds(new Set());
      dispatch(updateData(resp));
      setShowTable(true);
    } catch (e) {
      const { hide } = cogoToast.error("Error loading approval requests.", {
        hideAfter: 15,
        onClick: () => { hide(); },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (ids) => {
    if (!ids || ids.length === 0) {
      const { hide } = cogoToast.error("No records selected.", {
        hideAfter: 5,
        onClick: () => { hide(); },
      });
      return;
    }
    const hasErrors = ids.some((id) => qtyErrors[id]);
    if (hasErrors) {
      const { hide } = cogoToast.error(
        "Please fix the quantity limit errors before approving.",
        { hideAfter: 15, onClick: () => { hide(); } }
      );
      return;
    }
    const quantitiesData = ids.map((id) => ({ id, quantity: quantities[id] }));
    try {
      setLoading(true);
      await approvalQueService.approveRequest(ids, quantitiesData);
      const newReqs = requests.filter((r) => !ids.includes(r.id));
      setRequests(newReqs);
      dispatch(updateData(newReqs));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
      const { hide } = cogoToast.success("Request(s) approved successfully!", {
        hideAfter: 5,
        onClick: () => { hide(); },
      });
    } catch (e) {
      const { hide } = cogoToast.error("Error approving request(s).", {
        hideAfter: 15,
        onClick: () => { hide(); },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeny = async (ids) => {
    if (!ids || ids.length === 0) {
      const { hide } = cogoToast.error("No records selected.", {
        hideAfter: 5,
        onClick: () => { hide(); },
      });
      return;
    }
    const quantitiesData = ids.map((id) => ({ id, quantity: quantities[id] }));
    try {
      setLoading(true);
      await approvalQueService.denyRequest(ids, quantitiesData);
      const newReqs = requests.filter((r) => !ids.includes(r.id));
      setRequests(newReqs);
      dispatch(updateData(newReqs));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
      const { hide } = cogoToast.success("Request(s) denied.", {
        hideAfter: 5,
        onClick: () => { hide(); },
      });
    } catch (e) {
      const { hide } = cogoToast.error("Error denying request(s).", {
        hideAfter: 15,
        onClick: () => { hide(); },
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFacilities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="h-screen overflow-hidden flex flex-col">
      <CipHeader />
      <div
        className={`inline-block customtop absolute z-50 ${
          loading ? "show" : "hidden"
        }`}
      >
        <Loader type="Oval" color="#00BFFF" height={50} width={50} timeout={300000} />
      </div>

      <div className="flex flex-col flex-1 overflow-hidden px-4 pt-4">

        {/* Facility selector */}
        <div className="bg-white w-8/12 mx-auto mb-3 rounded shadow-md flex-shrink-0">
          <div className="tableHeader flex clearboth shadow-md py-6 pl-3 w-full">
            <div className="w-full text-darkclr font-semibold leading-10">
              <span className="text-xl">FL STOCK APPROVAL </span>
            </div>
          </div>
          <div className="px-12 py-4">
            <ul>
              <li className="inline-block text-xs leading-6 text-menu_txt w-full mb-3">
                <label className="text-labelclr font-semibold text-sm tracking-wider">
                  Facility
                </label>
                <span className="text-xs md:text-sm text-hlred ml-1">*</span>
                <select
                  name="facility"
                  aria-label="Select Facility"
                  ref={register({ required: false })}
                  onChange={(e) => loadRequestsByFacility(e.target.value)}
                  className="border focus:outline-none h-11 rounded-md pl-2 font-normal w-full text-fogotpwsd_login text-sm"
                >
                  <option value="0">Select Facility</option>
                  {facList.map((facility, i) => (
                    <option key={i} value={facility.id}>
                      {facility.name}
                    </option>
                  ))}
                </select>
              </li>
            </ul>
          </div>
        </div>

        {/* Table card */}
        <div
          className={`bg-white w-8/12 mx-auto flex flex-col flex-1 overflow-hidden rounded shadow-md mb-2 ${
            showTable ? "show" : "hidden"
          }`}
        >
          {/* Search */}
          <div className="flex-shrink-0 text-xs stockOrderList w-full text-right">
            <span className="relative">
              <input
                type="search"
                defaultValue={tableProps.searchText}
                onChange={(event) => { dispatch(search(event.currentTarget.value)); }}
                placeholder=" Search"
                className="border rounded w-2/12 h-10 mr-3 focus:outline-none focus:none pl-1 font-light align-bottom mt-3"
              />
              <img
                src="/img/search-2.svg"
                alt=""
                className="absolute w-4 h-4 right-5 -top-2 bottom-3"
              />
            </span>
          </div>

          {/* Table */}
          <div className="p-2 text-xs gridList overflow-y-auto overflow-x-auto flex-1 min-h-0 hide-scrollbar">
            <Table
              {...tableProps}
              childComponents={{
                headCell: {
                  content: (props) => {
                    if (props.column.key === "checkbox") {
                      return (
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={handleSelectAll}
                          className="cursor-pointer"
                        />
                      );
                    }
                  },
                },
                dataRow: {
                  elementAttributes: () => ({
                    style: { height: "52px" },
                  }),
                },
                cellText: {
                  content: (props) => {
                    switch (props.column.key) {
                      case "checkbox": {
                        return (
                          <input
                            type="checkbox"
                            checked={selectedIds.has(props.rowData.id)}
                            onChange={() => handleSelectRow(props.rowData.id)}
                            className="cursor-pointer"
                          />
                        );
                      }
                      case "Quantity": {
                        const rowId = props.rowData.id;
                        return (
                          <div>
                            <input
                              type="number"
                              min="1"
                              value={quantities[rowId] ?? props.rowData?.qnty ?? ""}
                              onChange={(e) => {
                                const val = parseInt(e.target.value, 10) || 0;
                                const qtyLimit = props.rowData.qty_limit > 0 ? Number(props.rowData.qty_limit) : null;
                                if (val <= 0) {
                                  setQtyErrors((prev) => ({
                                    ...prev,
                                    [rowId]: "Quantity must be at least 1",
                                  }));
                                } else if (qtyLimit && val > qtyLimit) {
                                  setQtyErrors((prev) => ({
                                    ...prev,
                                    [rowId]: `Quantity exceeds the allowed limit of ${qtyLimit}`,
                                  }));
                                } else {
                                  setQtyErrors((prev) => {
                                    const next = { ...prev };
                                    delete next[rowId];
                                    return next;
                                  });
                                  setQuantities((prev) => ({ ...prev, [rowId]: val }));
                                }
                              }}
                              className="h-8 rounded px-2 text-sm font-normal w-16 border focus:outline-none"
                            />
                            {qtyErrors[rowId] && (
                              <span className="text-hlred text-xs mt-1 block">
                                {qtyErrors[rowId]}
                              </span>
                            )}
                          </div>
                        );
                      }
                      case "category":
                        return (
                          <span className="inline-block text-xs">
                            {props.rowData.category || "\u2014"}
                          </span>
                        );
                      case "Description":
                        return (
                          <span className="inline-block text-xs">
                            {props.rowData.description || "\u2014"}
                          </span>
                        );
                      case "pre_Ord_Date":
                        return <span>{props.rowData?.pre_Ord_Date || ""}</span>;
                     case "LastOrderQty":
                        return (
                          <span className="inline-block text-xs">
                            {props.rowData.pre_Ord_Qty != null ? Number(props.rowData.pre_Ord_Qty) : "\u2014"}
                          </span>
                        );
                      case "Actions":
                        return <ActionsCell {...props} />;
                      default:
                        return null;
                    }
                  },
                },
              }}
              dispatch={dispatch}
            />
          </div>

          {/* Bulk action buttons */}
          <div className="flex-shrink-0 w-full px-2 py-3 border-t flex justify-end gap-2">
            <input
              type="button"
              onClick={() => handleApprove([...selectedIds])}
              value="Approve"
              className="bg-header_bg rounded py-2 px-7 cursor-pointer text-xs font-bold text-white h-10 shadow-md"
            />
            <input
              type="button"
              onClick={() => handleDeny([...selectedIds])}
              value="Deny"
              className="rounded py-2 px-7 cursor-pointer text-xs font-bold text-white h-10 shadow-md"
              style={{ backgroundColor: "#e05a5a" }}
            />
          </div>
        </div>

      </div>
    </div>
  );
};

export default withRouter(
  connect(
    () => ({}),
    (dispatch) => bindActionCreators({}, dispatch)
  )(ApprovalQue)
);